import React, { useEffect, useState } from 'react';
import { Table, Input, Breadcrumb, DatePicker, Space, Button, Tag, Grid, Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import { RightOutlined, FileExcelOutlined, FilePdfOutlined, DownloadOutlined } from '@ant-design/icons';
import { LoadableContent } from '../../components';
import { vehiculesServices } from '../../common/services';
import moment from 'moment';
import 'moment/locale/fr';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Vehicules = () => {
  const [vehicules, setVehicules] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;                 // < md
  const controlSize = isMobile ? 'small' : 'middle';
  const fullW = { width: isMobile ? '100%' : 'auto' };
  const inputW = isMobile ? '100%' : 320;       // largeur du champ recherche


  useEffect(() => {
    setLoading(true);
    vehiculesServices.recupererListeVehicules()
      .then((data) => setVehicules(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Erreur lors du chargement des véhicules :", err))
      .finally(() => setLoading(false));
  }, []);

  const filteredData = vehicules.filter((item) => {
    const search = searchTerm.toLowerCase();

    const matchText =
      item.marque?.toLowerCase().includes(search) ||
      item.modele?.toLowerCase().includes(search) ||
      item.immatriculation?.toLowerCase().includes(search);

    // Filtrage date depuis string déjà formatée
    const matchDate = selectedDate
      ? moment(item.createdAt, 'DD/MM/YYYY HH:mm:ss').format('YYYY-MM-DD') === moment(selectedDate).format('YYYY-MM-DD')
      : true;

    return matchText && matchDate;
  });

  // 🔁 Export Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData.map(item => ({
      Marque: item.marque,
      Modele: item.modele,
      Immatriculation: item.immatriculation,
      Couleur: item.couleur,
      Année: item.annee,
      Catégories: item.categories?.join(', '),
      Date: item.createdAt,
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Véhicules");
    XLSX.writeFile(workbook, "vehicules_tigoon.xlsx");
  };

  // 🔁 Export CSV
  const exportToCSV = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData.map(item => ({
      Marque: item.marque,
      Modele: item.modele,
      Immatriculation: item.immatriculation,
      Couleur: item.couleur,
      Année: item.annee,
      Catégories: item.categories?.join(', '),
      Date: item.createdAt,
    })));
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'vehicules_tigoon.csv');
  };

  // 🔁 Export PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Liste des véhicules TIGOON", 14, 10);

    autoTable(doc, {
      startY: 20,
      head: [['Marque', 'Modèle', 'Immatriculation', 'Couleur', 'Année', 'Catégories', 'Date']],
      body: filteredData.map(item => [
        item.marque,
        item.modele,
        item.immatriculation,
        item.couleur,
        item.annee,
        item.categories?.join(', '),
        item.createdAt
      ]),
    });

    doc.save("vehicules_tigoon.pdf");
  };

  const columns = [
    { title: 'Marque', dataIndex: 'marque', key: 'marque' },
    { title: 'Modèle', dataIndex: 'modele', key: 'modele' },
    {
      title: 'Immatriculation',
      dataIndex: 'immatriculation',
      key: 'immatriculation',
      sorter: (a, b) => a.immatriculation.localeCompare(b.immatriculation)
    },
    { title: 'Couleur', dataIndex: 'couleur', key: 'couleur' },
    { title: 'Année', dataIndex: 'annee', key: 'annee' },
    {
      title: 'Catégorie',
      dataIndex: 'categories',
      key: 'categories',
      render: (categories) =>
        categories?.map((cat, index) => (
          <Tag color={cat === 'econom' ? 'green' : 'blue'} key={index}>
            {cat.toUpperCase()}
          </Tag>
        )),
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => {
        const aDate = moment(a.createdAt, 'DD/MM/YYYY HH:mm:ss').toDate();
        const bDate = moment(b.createdAt, 'DD/MM/YYYY HH:mm:ss').toDate();
        return aDate - bDate;
      },
    },
  ];

  return (
    // 🔹 Remplace tout ton bloc par ceci :
    <LoadableContent isLoading={isLoading}>
      <div className="content-wrapper">
        <div className="container-xxl flex-grow-1 container-p-y">
          <div className="row">
            <div className="col-md-12">
              <Breadcrumb
                separator={<RightOutlined style={{ fontSize: 16, color: '#999', fontWeight: 'bolder' }} />}
                style={{ marginBottom: 16 }}
              >
                <Breadcrumb.Item>
                  <Link to="/dashboard" style={{ fontSize: 16, fontWeight: 'bolder' }}>🏠 ACCUEIL</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <span style={{ fontSize: 16, fontWeight: 'bolder' }}>💸 LISTE DES VÉHICULES TIGOON</span>
                </Breadcrumb.Item>
              </Breadcrumb>

              <div className="card mb-4">
                <div className="card-body">

                  {/* 🔎 Filtres (stack en mobile, côte à côte desktop) */}
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={16}>
                      <Input
                        placeholder="Rechercher marque, modèle ou immatriculation"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: inputW }}
                        allowClear
                        size={controlSize}
                      />
                    </Col>
                    <Col xs={24} md={8} style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                      <DatePicker
                        onChange={(date) => setSelectedDate(date)}
                        placeholder="Filtrer par date"
                        format="DD/MM/YYYY"
                        allowClear
                        size={controlSize}
                        style={fullW}
                      />
                    </Col>
                  </Row>

                  {/* ⬇️ Export (wrap + plein-largeur en mobile) */}
                  <div style={{ marginBottom: 16 }}>
                    <Space wrap size={[8, 8]} style={{ width: '100%' }}>
                      <Button
                        icon={<FileExcelOutlined />}
                        onClick={exportToExcel}
                        type="primary"
                        size={controlSize}
                        style={fullW}
                      >
                        Exporter Excel (.xlsx)
                      </Button>

                      <Button
                        icon={<DownloadOutlined />}
                        onClick={exportToCSV}
                        type="default"
                        size={controlSize}
                        style={fullW}
                      >
                        Exporter CSV
                      </Button>

                      <Button
                        icon={<FilePdfOutlined />}
                        onClick={exportToPDF}
                        danger
                        size={controlSize}
                        style={fullW}
                      >
                        Exporter PDF
                      </Button>
                    </Space>
                  </div>

                  {/* 📋 Table */}
                  <Table
                    columns={columns}
                    dataSource={filteredData.map((item) => ({
                      ...item,
                      key: item._id,
                    }))}
                    pagination={{ pageSize: 10, showLessItems: isMobile }}
                    scroll={{ x: true }}
                    size={controlSize}
                  />

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoadableContent>
  );
};

export default Vehicules;
